import React, { useState, useEffect, useRef, useMemo } from 'react';
import './TradeDesk.css';
import { subscribeToLoadingState } from '../../services/websocketApi/orders';
import websocketService from '../../services/websocket';
import { getInstrumentsBySymbol } from '../../services/websocketApi/instruments';
import { useUserDataContext } from '../../contexts/UserDataContext';

// Import modular components
import PriceDisplay from './PriceDisplay';
import OrderForm from './OrderForm/index';
import { marketDataCache, cleanupCache, formatCurrency, extractPriceData, isValidMarket } from './utils';

// Helper to ensure values are valid numbers, not NaN
const ensureValidNumber = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return 0; // Use 0 as fallback
  }
  return value;
};

// Main TradeDesk component
const TradeDesk = ({ market, livePrices }) => {
  // Store previous price values for comparison
  const prevPriceRef = useRef(null);
  
  // Add local loading state
  const [isLoading, setIsLoading] = useState(false);
  
  // Add state for direct price updates from WebSocket
  const [directPrices, setDirectPrices] = useState({
    bid: null,
    ask: null,
    timestamp: 0
  });
  
  // Add state for instrument details fetched from API
  const [fetchedInstrumentDetails, setFetchedInstrumentDetails] = useState(null);

  // Access UserDataContext to get cached instrument data
  const { instrumentsData } = useUserDataContext();
  
  // Add cache key for current market data
  const marketCacheKey = useMemo(() => {
    if (!market) return null;
    // Create a unique key from market ID and current prices
    const marketId = market.originalId || market.id;
    if (!marketId) return null;
    
    // Include price data in cache key to update when prices change significantly
    const priceData = livePrices && livePrices[marketId] 
      ? `${livePrices[marketId].bidPrice}-${livePrices[marketId].askPrice}` 
      : `${market.price || market.lastPrice || 0}`;
    
    return `${marketId}-${priceData}`;
  }, [market, livePrices]);
  
  // Clear old cache entries periodically
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      cleanupCache();
    }, 60 * 1000); // Run cleanup every minute
    
    return () => clearInterval(cleanupInterval);
  }, []);

  // Subscribe to loading state changes
  useEffect(() => {
    const unsubscribe = subscribeToLoadingState((operation, isLoading) => {
      console.log(`Loading state update in TradeDesk: ${operation} is ${isLoading}`);
      if (operation === 'placeOrder') {
        setIsLoading(isLoading);
      }
    });
    
    // Clean up subscription on unmount
    return () => unsubscribe();
  }, []);

  // Subscribe directly to price updates from WebSocket
  useEffect(() => {
    if (!market) return;
    
    // Get symbol in proper format for WebSocket subscription
    const symbol = market.originalId || market.symbol;
    if (!symbol) return;
    
    // console.log(`[TradeDesk] Setting up direct price subscription for ${symbol}`);
    // Handler for price updates
    const handlePriceUpdate = (priceData) => {
      if (!priceData || !priceData.symbol) return;
      
      // Check if this update is for our selected market
      if (symbol.includes(priceData.symbol) || priceData.symbol.includes(symbol)) {
        // console.log(`[TradeDesk] Received direct price update: ${JSON.stringify(priceData)}`);
        
        // Update local state with direct prices
        setDirectPrices({
          bid: priceData.bid !== null ? priceData.bid : directPrices.bid,
          ask: priceData.ask !== null ? priceData.ask : directPrices.ask,
          timestamp: Date.now()
        });
      }
    };
    
    // Register for WebSocket price updates
    websocketService.on('price-feed', handlePriceUpdate);
    
    // Also check if we have cached prices
    if (window.__instrumentsData && window.__instrumentsData.length > 0) {
      // Find our instrument
      const instrumentData = window.__instrumentsData.find(inst => 
        inst.symbol === symbol || inst.symbol.includes(symbol.split('.').pop())
      );
      
      if (instrumentData) {
        // console.log(`[TradeDesk] Found cached instrument data: ${JSON.stringify(instrumentData)}`);
      }
    }
    
    // Cleanup on unmount
    return () => {
      websocketService.off('price-feed', handlePriceUpdate);
    };
  }, [market, directPrices]);

  // Helper function to find instrument in cache (similar to ClosePositionModal)
  const findInstrumentInCache = (symbol) => {
    if (!symbol) return null;
    
    // Check different possible locations where instrument data might be stored
    
    // Check in instruments array (primary location)
    if (Array.isArray(instrumentsData?.instruments)) {
      const match = instrumentsData.instruments.find(inst => inst.symbol === symbol);
      if (match) return match;
    }
    
    // Check in instrumentsById object (if exists)
    if (instrumentsData?.instrumentsById && instrumentsData.instrumentsById[symbol]) {
      return instrumentsData.instrumentsById[symbol];
    }
    
    // Check in instrumentsData directly (some implementations store flat)
    if (Array.isArray(instrumentsData)) {
      const match = instrumentsData.find(inst => inst.symbol === symbol);
      if (match) return match;
    }
    
    // Check in window cache as last resort
    if (window.__instrumentsData && Array.isArray(window.__instrumentsData)) {
      return window.__instrumentsData.find(item => item.symbol === symbol);
    }
    
    return null;
  };

  // Fetch instrument details directly from the API when market changes
  useEffect(() => {
    if (!market) return;
    
    // Get symbol in proper format for API request
    const symbol = market.originalId || market.symbol;
    if (!symbol) return;
    
    console.log(`[TradeDesk] Looking up instrument details for ${symbol}`);
    
    // Reset previous instrument details
    setFetchedInstrumentDetails(null);
    
    // Check if we already have the instrument data in the cache
    const cachedInstrument = findInstrumentInCache(symbol);
    
    if (cachedInstrument) {
      console.log(`[TradeDesk] Using cached instrument data for ${symbol}:`, cachedInstrument);
      setFetchedInstrumentDetails(cachedInstrument);
    } else {
      // Fallback: Fetch instrument details if not in cache
      console.log(`[TradeDesk] Instrument not found in cache, fetching from API: ${symbol}`);
      
      // Fetch instrument details including min/max order size
      getInstrumentsBySymbol(symbol)
        .then(instruments => {
          if (instruments && instruments.length > 0) {
            const instrumentDetails = instruments[0];
            console.log(`[TradeDesk] Fetched instrument details for ${symbol}:`, instrumentDetails);
            
            // Save the fetched details
            setFetchedInstrumentDetails(instrumentDetails);
          } else {
            console.warn(`[TradeDesk] Could not find instrument details for ${symbol}`);
          }
        })
        .catch(error => {
          console.error(`[TradeDesk] Error fetching instrument details for ${symbol}:`, error);
        });
    }
  }, [market, instrumentsData]);

  // Look up instrument details from WebSocket cache or use fetched details
  const instrumentDetails = useMemo(() => {
    if (!market) return null;
    
    // Get the most up-to-date instrument details from cache or fallback to fetched details
    const cachedInstrument = findInstrumentInCache(market.originalId || market.symbol);
    return cachedInstrument || fetchedInstrumentDetails;
  }, [market, fetchedInstrumentDetails, instrumentsData]);

  // Determine if we have valid market data to show
  const hasValidMarket = useMemo(() => {
    // We need both market data and live prices to be valid
    if (!market || !instrumentDetails) return false;
    
    // Check if the market is valid for trading
    return isValidMarket(market, livePrices, directPrices);
  }, [market, livePrices, directPrices, instrumentDetails]);

  // Extract price data using the utility function
  const { symbol, price: extractedPrice, bid: extractedBid, ask: extractedAsk, prevClose: extractedPrevClose } = extractPriceData(market, livePrices, directPrices);
  
  // Ensure we don't have NaN values
  const currentPrice = ensureValidNumber(extractedPrice);
  const bid = ensureValidNumber(extractedBid);
  const ask = ensureValidNumber(extractedAsk);
  const previousClose = ensureValidNumber(extractedPrevClose);
  
  // Track currently displayed prices in state
  const [displayPrice, setDisplayPrice] = useState(0);
  const [displayPrevClose, setDisplayPrevClose] = useState(0);
  const [displayBid, setDisplayBid] = useState(0);
  const [displayAsk, setDisplayAsk] = useState(0);
  
  // Track previous prices to detect changes
  const prevBidRef = useRef(null);
  const prevAskRef = useRef(null);
  const prevMarketRef = useRef(null);

  // Add state for price change animations
  const [bidFlashClass, setBidFlashClass] = useState('');
  const [askFlashClass, setAskFlashClass] = useState('');
  const [marketFlashClass, setMarketFlashClass] = useState('');
  const [priceFlashClass, setPriceFlashClass] = useState('');

  // Monitor market changes for animation
  useEffect(() => {
    if (market && prevMarketRef.current && market.name !== prevMarketRef.current.name) {
      // Market changed, trigger animation
      setMarketFlashClass('market-flash');
      
      // Remove animation class after it completes
      const timer = setTimeout(() => {
        setMarketFlashClass('');
      }, 800); // Slightly longer animation for market
      
      return () => clearTimeout(timer);
    }
    
    // Update previous market reference
    prevMarketRef.current = market;
  }, [market]);

  // Update display prices when market data changes
  useEffect(() => {
    // Only update when prices actually change to avoid unnecessary re-renders
    if (prevPriceRef.current !== currentPrice || 
        displayBid !== bid || 
        displayAsk !== ask) {
      
      // console.log('[TradeDesk] Updating displayed prices:', { 
      //   currentPrice, 
      //   bid, 
      //   ask,
      //   previousClose 
      // });
      
      prevPriceRef.current = currentPrice;
      setDisplayPrice(currentPrice);
      setDisplayPrevClose(previousClose);
      setDisplayBid(bid);
      setDisplayAsk(ask);
    }
  }, [currentPrice, bid, ask, previousClose]);

  // Monitor main price changes and trigger animations
  useEffect(() => {
    if (prevPriceRef.current !== null && displayPrice !== prevPriceRef.current) {
      // Price changed, determine direction for animation
      if (displayPrice > prevPriceRef.current) {
        setPriceFlashClass('price-flash-up');
      } else if (displayPrice < prevPriceRef.current) {
        setPriceFlashClass('price-flash-down');
      } else {
        setPriceFlashClass('price-flash');
      }
      
      // Remove animation class after it completes
      const timer = setTimeout(() => {
        setPriceFlashClass('');
      }, 600); // Match animation duration
      
      return () => clearTimeout(timer);
    }
  }, [displayPrice]);

  // Monitor price changes and trigger animations
  useEffect(() => {
    if (prevBidRef.current !== null && displayBid !== prevBidRef.current) {
      // Price changed, determine direction for animation
      if (displayBid > prevBidRef.current) {
        setBidFlashClass('price-flash-up');
      } else if (displayBid < prevBidRef.current) {
        setBidFlashClass('price-flash-down');
      } else {
        setBidFlashClass('price-flash');
      }
      
      // Remove animation class after it completes
      const timer = setTimeout(() => {
        setBidFlashClass('');
      }, 600); // Match animation duration
      
      return () => clearTimeout(timer);
    }
    
    // Update previous price reference
    prevBidRef.current = displayBid;
  }, [displayBid]);

  // Similar effect for ask price
  useEffect(() => {
    if (prevAskRef.current !== null && displayAsk !== prevAskRef.current) {
      // Price changed, determine direction for animation
      if (displayAsk > prevAskRef.current) {
        setAskFlashClass('price-flash-up');
      } else if (displayAsk < prevAskRef.current) {
        setAskFlashClass('price-flash-down');
      } else {
        setAskFlashClass('price-flash');
      }
      
      // Remove animation class after it completes
      const timer = setTimeout(() => {
        setAskFlashClass('');
      }, 600); // Match animation duration
      
      return () => clearTimeout(timer);
    }
    
    // Update previous price reference
    prevAskRef.current = displayAsk;
  }, [displayAsk]);

  // Format currency using the utility function
  const formatCurrencyWithContext = (value) => {
    // Ensure value is not NaN before formatting
    if (value === null || value === undefined || isNaN(value)) {
      return '';
    }
    return formatCurrency(value, market, instrumentDetails);
  };

  // Use instrumentDetails for getting decimals, min/max quantities
  const getDisplayDecimals = () => {
    if (!instrumentDetails) return 5; // Default fallback
    
    // Use the instrument details to get proper decimal places
    return instrumentDetails.pipPosition || 
           instrumentDetails.decimalPlaces || 
           instrumentDetails.pipDecimals || 5;
  };

  // Show placeholder when no valid market is selected
  if (!hasValidMarket) {
    return (
      <div className="td-container">
        <div className="td-placeholder">
          <div className="td-placeholder-message">Select a market to trade</div>
        </div>
      </div>
    );
  }

  // Display PriceDisplay component only if displayPrice is not NaN
  const shouldDisplayPrice = !isNaN(displayPrice) && displayPrice !== 0;

  return (
    <div className="td-container">
      <div className="td-form">
        <PriceDisplay 
          market={market}
          displayPrice={displayPrice}
          displayBid={displayBid}
          displayAsk={displayAsk}
          previousClose={displayPrevClose}
          formatCurrency={formatCurrencyWithContext}
          priceFlashClass={`td-${priceFlashClass}`}
          bidFlashClass={`td-${bidFlashClass}`}
          askFlashClass={`td-${askFlashClass}`}
          marketFlashClass={`td-${marketFlashClass}`}
        />

        <OrderForm 
          market={market}
          bid={displayBid}
          ask={displayAsk}
          currentPrice={currentPrice}
          displayPrice={displayPrice}
          formatCurrency={formatCurrencyWithContext}
          getInstrumentDetails={instrumentDetails}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default TradeDesk; 